import { Box, BoxProps } from '@material-ui/core';

import { useCustomTheme } from '@/hooks/useCustomTheme';

import { StatusTagType } from '../../../../config/types';
import { Font } from '../Font';
import { Text } from '../Text';

interface StatusTagProps extends BoxProps {
  type: StatusTagType | 'minor';
  labelTextId: string;
  labelDynamicDataTexts?: string[];
  variant?: 'badge' | 'text' | 'outlined' | 'contained';
  colorType?: 'main' | 'dark' | 'light';
  startIcon?: string;
  endIcon?: string;
  size?: 'small' | 'medium';
}

const StatusTag = ({
  type,
  labelTextId,
  labelDynamicDataTexts,
  startIcon,
  endIcon,
  variant = 'contained',
  colorType = 'main',
  size = 'medium',
  ...props
}: StatusTagProps) => {
  const { palette, customBorderRadius } = useCustomTheme();

  const color =
    type === StatusTagType.DEFAULT
      ? colorType === 'main'
        ? palette.text.secondary
        : colorType === 'dark'
        ? palette.text.primary
        : palette.text.secondary
      : palette[type]?.[colorType];

  if (!color) {
    return null;
  }

  if (variant === 'badge') {
    return (
      <Box
        borderRadius={customBorderRadius.chip}
        width={size === 'small' ? 12 : 24}
        height={size === 'small' ? 12 : 24}
        style={{
          backgroundColor: color,
          opacity: palette.type === 'dark' ? 0.8 : 0.6,
        }}
      />
    );
  }

  return (
    <Box
      px={1}
      position="relative"
      height={size === 'small' ? 20 : 24}
      display={'inline-flex'}
      justifyContent={'space-between'}
      alignItems={'center'}
      borderRadius={customBorderRadius.chip}
      boxSizing={'border-box'}
      border={variant === 'outlined' ? `1px solid ${color}` : null}
      overflow="hidden"
      {...props}
    >
      {variant === 'contained' && (
        <Box
          style={{
            backgroundColor: color,
            position: 'absolute',
            inset: 0,
            opacity: palette.type === 'dark' ? 0.25 : 0.1,
          }}
        />
      )}
      {startIcon && (
        <Box pr={'2px'} display={'flex'}>
          <i className="material-icons-outlined" style={{ lineHeight: 1.5, fontSize: 14, color: color }}>
            {startIcon}
          </i>
        </Box>
      )}
      {labelTextId && (
        <Box display={'flex'}>
          <Font
            variant="caption"
            style={{ lineHeight: 1, color: color, whiteSpace: 'nowrap', ...(size === 'small' && { fontSize: 10 }) }}
          >
            <strong>
              <Text tId={labelTextId} dynamicDataTexts={labelDynamicDataTexts} />
            </strong>
          </Font>
        </Box>
      )}
      {endIcon && (
        <Box pl={size === 'small' ? '2px' : '4px'} display={'flex'}>
          <i
            className="material-icons-outlined"
            style={{ lineHeight: 1.5, fontSize: size === 'small' ? 12 : 16, color: color }}
          >
            {endIcon}
          </i>
        </Box>
      )}
    </Box>
  );
};

export { StatusTag };
